import { FC, ReactNode, useState } from 'react'
import { Copy, Check, Maximize2, Stethoscope, ClipboardList, ListChecks, FileText } from 'lucide-react'
import { Button } from '../../ui/button'
import { DocumentBody } from '../../document/DocumentBody'
import { cn } from '../../../lib/utils'
import type { CanvasUpdate } from '../../../contexts/ChatContext'
import { TAB_LABELS } from '../../../utils/canvasUtils'

const TAB_COLORS: Record<string, { bg: string; border: string; text: string; accent: string; icon: ReactNode }> = {
  hpi: {
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/20',
    text: 'text-blue-500',
    accent: 'text-blue-600 dark:text-blue-400',
    icon: <Stethoscope className="h-3.5 w-3.5" />
  },
  pe: {
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/20',
    text: 'text-emerald-500',
    accent: 'text-emerald-600 dark:text-emerald-400',
    icon: <ListChecks className="h-3.5 w-3.5" />
  },
  assessment_and_plan: {
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/20',
    text: 'text-amber-500',
    accent: 'text-amber-600 dark:text-amber-400',
    icon: <ClipboardList className="h-3.5 w-3.5" />
  },
  notes: {
    bg: 'bg-violet-500/10',
    border: 'border-violet-500/20',
    text: 'text-violet-500',
    accent: 'text-violet-600 dark:text-violet-400',
    icon: <FileText className="h-3.5 w-3.5" />
  },
  ipass: {
    bg: 'bg-violet-500/10',
    border: 'border-violet-500/20',
    text: 'text-violet-500',
    accent: 'text-violet-600 dark:text-violet-400',
    icon: <FileText className="h-3.5 w-3.5" />
  },
  custom: {
    bg: 'bg-gray-500/10',
    border: 'border-gray-500/20',
    text: 'text-gray-500',
    accent: 'text-gray-600 dark:text-gray-400',
    icon: <FileText className="h-3.5 w-3.5" />
  }
}

/** Map legacy/alias tab keys to their canonical TAB_COLORS entry. */
const TAB_COLOR_ALIASES: Record<string, string> = {
  ap: 'assessment_and_plan',
}

/** Resolve a tab key (canonical or legacy alias) to its color/icon config. */
const resolveTabColors = (tab: string) =>
  TAB_COLORS[tab] || TAB_COLORS[TAB_COLOR_ALIASES[tab]] || TAB_COLORS.custom

interface InlineCanvasProps {
  canvasUpdates: CanvasUpdate[]
  onOpenCanvas?: (tab: string) => void
}

/**
 * Compact inline canvas preview embedded inside a chat message.
 * Shows tabbed read-only preview of canvas updates. Click to open full canvas.
 */
export const InlineCanvas: FC<InlineCanvasProps> = ({ canvasUpdates, onOpenCanvas }) => {
  const [activeTab, setActiveTab] = useState(canvasUpdates[0]?.tab || 'hpi')
  const [copied, setCopied] = useState(false)

  const tabs = canvasUpdates.map(u => u.tab)
  const activeUpdate = canvasUpdates.find(u => u.tab === activeTab)
  if (!activeUpdate) return null

  const colors = resolveTabColors(activeTab)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(activeUpdate.content)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      console.error('Failed to copy to clipboard:', error)
    }
  }

  return (
    <div className={cn('mt-2 rounded-xl border overflow-hidden', colors.border, colors.bg)}>
      {/* Tab bar */}
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-border/50">
        <div className="flex items-center gap-2">
          <div
            data-testid="inline-canvas-icon-tile"
            className={cn('w-6 h-6 rounded-lg flex items-center justify-center', colors.bg, colors.text)}
          >
            {colors.icon}
          </div>
          <div className="flex items-center gap-1">
            {tabs.map(tab => {
              const tc = resolveTabColors(tab)
              return (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={cn(
                    'px-2 py-0.5 text-[10px] font-semibold rounded-full transition-all',
                    tab === activeTab
                      ? `${tc.bg} ${tc.text} border ${tc.border}`
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  {TAB_LABELS[tab] || tab}
                </button>
              )
            })}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {activeUpdate.version != null && (
            <span className="text-[9px] text-muted-foreground tabular-nums mr-1">
              v{activeUpdate.version}
            </span>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCopy}
            className="h-6 w-6 p-0 rounded-md hover:bg-background"
            title="Copy to clipboard"
            aria-label={`Copy ${TAB_LABELS[activeTab] || activeTab} to clipboard`}
          >
            {copied ? (
              <Check className="h-3 w-3 text-emerald-500" aria-hidden="true" data-testid="copy-state-copied" />
            ) : (
              <Copy className="h-3 w-3" aria-hidden="true" data-testid="copy-state-idle" />
            )}
          </Button>
          {onOpenCanvas && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onOpenCanvas(activeTab)}
              className="h-6 w-6 p-0 rounded-md hover:bg-background"
              title="Open in full canvas editor"
              aria-label={`Open ${TAB_LABELS[activeTab] || activeTab} in canvas`}
            >
              <Maximize2 className="h-3 w-3" aria-hidden="true" />
            </Button>
          )}
        </div>
      </div>

      {/* Content preview */}
      <div className="px-3 py-2 text-xs leading-relaxed max-h-[200px] overflow-y-auto">
        <DocumentBody content={activeUpdate.content} />
      </div>
    </div>
  )
}
